<template>
    <div class="employee-section__inner">
        <ul class="filter">
            <li class="filter__item">
                <div class="filter__item-first">{{ dictionary.FilterEmployees }}</div>
            </li>
            <li class="filter__item" v-if="solutions">
                <label for="solutionFilter" class="sr-only">{{ dictionary.EmployeesSolutionLabel }}</label>
                <select id="solutionFilter" v-model="solution" :disabled="isLoading">
                    <option value="All">{{ dictionary.EmployeesSolutionLabel }}</option>
                    <option v-for="solutionItem in solutions" :key="solutionItem.Key" :value="solutionItem.Key">
                        {{ solutionItem.Name }}
                    </option>
                </select>
            </li>            
            <li class="filter__item" v-if="departments">
                <label for="departmentFilter" class="sr-only">{{ dictionary.EmployeesDepartmentLabel }}</label>
                <select id="departmentFilter" v-model="department" :disabled="isLoading">
                    <option value="All">{{ dictionary.EmployeesDepartmentLabel }}</option>
                    <option v-for="departmentItem in departments" :key="departmentItem.Key" :value="departmentItem.Key">
                        {{ departmentItem.Name }}
                    </option>
                </select>
            </li>
            <li class="filter__item" v-if="countries">
                <label for="countryFilter" class="sr-only">{{ dictionary.EmployeesCountryLabel }}</label>
                <select id="countryFilter" v-model="country" :disabled="isLoading">
                    <option value="All">{{ dictionary.EmployeesCountryLabel }}</option>
                    <option v-for="country in countries" :key="country.Key" :value="country.Key">
                        {{ country.Name }}
                    </option>
                </select>
            </li>
            <li class="filter__item" v-if="hasFilterApplied">
                <button type="button" @click="resetFilter">{{ dictionary.EmployeesResetFilter }}</button>
            </li>
        </ul>
        <ul class="employee-container" v-if="groupedEmployees">
            <li class="employee-container-item" v-for="(group, key) in groupedEmployees" :key="key">
                <p class="employee-department">{{ key }}</p>
                <ul class="employee-items">
                    <li class="employee-item" v-for="(employee, index) in group" :key="employee.Email">
                        <span :id="index + 1" class="custom-anchor"></span>
                        <div class="employee-item__image">
                            <ResponsiveImage v-if="employee.HasImage" :imageModel="employee.ImageForVue" />
                        </div>
                        <div class="employee-item__info">
                            <div class="employee-item__row">
                                <h3 class="employee-item__name">{{ employee.Name }}</h3>
                                <p class="employee-item__title" v-if="employee.HasTitle">{{ employee.Title }}</p>
                            </div>
                            <div class="employee-item__row">
                                <p v-if="employee.HasPhoneNumbers" v-for="number in employee.PhoneNumbers" class="employee-item__text">
                                    <a :href="'tel:' + number " class="employee-item__link">{{ number }}</a>
                                </p>
                                <p v-if="employee.HasEmail" class="employee-item__text">
                                    <a :href="'mailto:' + employee.Email" class="employee-item__link">{{ employee.Email }}</a>
                                </p>
                                <p v-if="employee.HasLinkedIn" class="employee-item__text">
                                    <a :href="employee.LinkedIn.Url" :target="employee.LinkedIn.Target" class="employee-item__link">{{ employee.LinkedIn.Name }}</a>
                                </p>
                            </div>
                        </div>
                    </li>
                </ul>
            </li>
        </ul>
        <div class="employees__footer">
            <div class="employees__no-employees" v-show="!employees && !isLoading">
                {{ dictionary.EmployeesNoResults }}
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue'
import { bindRoute } from "./mixins/bindroute.js";
import ResponsiveImage from "./components/responsiveImage.vue";

const serviceUrl = "/umbraco/api/employeesApi/getEmployees",
    defaultDepartment = "All",
    defaultCountry = "All",
    defaultSolution = "All";

export default {
    name: "employees",
    components: {
        ResponsiveImage
    },
    data() {
        return {
            dictionary: [],
            departments: [],
            solutions: [],
            countries: [],
            employees: [],
            pageSize: 16,
            totalEmployees: 0,
            loadedEmployees: 0,
            isLoading: false,
            hasLoadedInitials: false,
            isResetting: false
        };
    },
    mixins: [
        bindRoute({
            page: 1,
            department: defaultDepartment,
            solution: defaultSolution,
            country: defaultCountry,
        }),
    ],
    watch: {
        page(oldVal, newVal) {
            this.handleFilterPropertyChange(oldVal, newVal, false, true);
        },
        department(oldVal, newVal) {
            this.handleFilterPropertyChange(oldVal, newVal, true, false);
        },
        solution(oldVal, newVal) {
            this.handleFilterPropertyChange(oldVal, newVal, true, false);
        },        
        country(oldVal, newVal) {
            this.handleFilterPropertyChange(oldVal, newVal, true, false);
        }
    },
    mounted() {
        this.$router.isReady().then(() => {
            this.getInitialEmployees().then(() => {
                this.scrollToAnchor();
            });
        }).catch(error => {
            console.log(error);
        });
    },
    methods: {
        handleFilterPropertyChange(oldVal, newVal, resetPage = false, append = false) {
            if (oldVal !== newVal) {
                if (this.hasLoadedInitials && !this.isResetting) {
                    this.filterChanged(resetPage, append);
                }
            }
        },
        getEmployees(append = false) {
            const vm = this,
                languageCode = document.querySelector("html").getAttribute("lang");
            vm.toggleSpinner(true);
            var object = {
                languageCode: languageCode,
            };

            if (vm.department != defaultDepartment) {
                object.department = vm.department;
            }

            if (vm.solution != defaultSolution) {
                object.solution = vm.solution;
            }            
            
            if (vm.country != defaultCountry) {
                object.country = vm.country;
            }

            fetch(serviceUrl + "?" + new URLSearchParams(object))
                .then((response) => response.json())
                .then((data) => {

                    if (append) {
                        vm.employees.push(...data.Items);
                        vm.loadedEmployees += data.Items.length;
                    } else {
                        vm.employees = data.Items;
                        vm.loadedEmployees = data.Items.length;
                    }
                    vm.totalEmployees = data.TotalItems;

                    vm.dictionary = data.Dictionary;
                    vm.solutions = data.Facets.Solutions;
                    vm.departments = data.Facets.Departments;
                    vm.countries = data.Facets.Countries;

                    nextTick(() => {
                        vm.hasLoadedInitials = true;
                        vm.toggleSpinner(false);
                        vm.isResetting = false;
                        this.scrollToAnchor(); // Scroll after employees are rendered
                    });

                })
                .catch((error) => {
                    console.log(error);
                    vm.toggleSpinner(false);
                });
        },
        getNextPage() {
            if (!this.isLoading) {
                this.page++;
            }
        },
        async getInitialEmployees() {
            await this.getEmployees(0, this.page * this.pageSize, true);
        },
        filterChanged(resetPage = false, append = false) {
            this.isResetting = true;
            if (!append || resetPage) {
                this.page = 1;
                this.getEmployees(append);
            } else {
                this.getEmployees(append);
            }
        },
        resetFilter() {
            const vm = this;
            vm.isResetting = true;
            vm.employees = [];
            vm.totalEmployees = 0;
            vm.loadedEmployees = 0;
            vm.page = 1;
            vm.department = defaultDepartment;
            vm.solution = defaultSolution;
            vm.country = defaultCountry;

            vm.$router.replace({ path: window.location.pathname })
                .then(() => {
                    vm.getEmployees();
                })
                .catch(err => { console.log(err); });
        },
        toggleSpinner(isLoading) {
            this.isLoading = isLoading;
            const bodyElement = document.querySelector("body");
            if (isLoading) {
                bodyElement.classList.add("is-loading");
            } else {
                bodyElement.classList.remove("is-loading");
            }
        },
        scrollToAnchor() {
            nextTick(() => {
                if (window.location.hash) {
                    const id = window.location.hash.slice(1);
                    const element = document.getElementById(id);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            });
        }
    },
    computed: {
        hasMoreEmployees() {
            return (
                this.totalEmployees !== 0 &&
                this.employees.length < this.totalEmployees
            );
        },
        hasFilterApplied() {
            return this.solution != defaultSolution || this.country != defaultCountry || this.department != defaultDepartment;
        },
        groupedEmployees() {
            if (this.employees) {
                return this.employees.reduce((groups, item) => {
                    const group = (groups[item.DepartmentName] || []);
                    group.push(item);
                    groups[item.DepartmentName] = group;
                    return groups;
                }, {});
            }
            return null;
        }
    },
};
</script>
